import { queryOptions } from '@tanstack/react-query';
import { z } from 'zod';
import { apiGet, apiPatch, apiPost } from '../../lib/api';

/**
 * Partenerii contabilității, dinspre client.
 *
 * Schemele Zod sunt declarate aici, nu importate din `@pcrm/be/partners`: `apps/crm` e
 * `scope:fe` și n-are voie să depindă de backend. Aceeași alegere ca la `customers.api.ts` —
 * granița se validează în ambele capete, iar un câmp redenumit pe server pică la `parse`, nu
 * ajunge `undefined` în formular.
 */

export const PARTNER_ANAF_STATUSES = ['active', 'inactive', 'struck_off'] as const;

export type PartnerAnafStatus = (typeof PARTNER_ANAF_STATUSES)[number];

const partnerSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  /** CIF **sau** CNP, în același câmp. `null` = necompletat. Vezi tichetul #114. */
  taxCode: z.string().nullable(),
  // Ce vine de la ANAF. Toate `null`-abile: la un partener persoană fizică lipsesc toate,
  // iar la o firmă ANAF chiar omite unele (cod poștal, telefon).
  tradeRegisterNo: z.string().nullable(),
  legalAddress: z.string().nullable(),
  city: z.string().nullable(),
  county: z.string().nullable(),
  postalCode: z.string().nullable(),
  phone: z.string().nullable(),
  /** ⚠️ Trei stări: `true` · `false` · `null` = **nu s-a verificat la ANAF**. */
  vatPayer: z.boolean().nullable(),
  vatOnCollection: z.boolean().nullable(),
  anafStatus: z.enum(PARTNER_ANAF_STATUSES).nullable(),
  anafStatusText: z.string().nullable(),
  anafCheckedAt: z.string().nullable(),
});

export type Partner = z.infer<typeof partnerSchema>;

/**
 * Ce trimite formularul.
 *
 * Textele sunt `''` când sunt goale — serverul le face `null`. Câmpurile de la ANAF își
 * păstrează tipul, pentru că nu se tastează: vin dintr-un răspuns și pleacă înapoi ca atare.
 *
 * ⚠️ **Nicio validare aici, și nici pe server** (fondatorul, 2026-09-15). Ecranul nu
 * verifică cifra de control a CUI-ului sau a CNP-ului și nu se plânge de duplicate.
 */
export interface PartnerInput {
  readonly name: string;
  readonly taxCode: string;
  readonly tradeRegisterNo: string;
  readonly legalAddress: string;
  readonly city: string;
  readonly county: string;
  readonly postalCode: string;
  readonly phone: string;
  readonly vatPayer: boolean | null;
  readonly vatOnCollection: boolean | null;
  readonly anafStatus: PartnerAnafStatus | null;
  readonly anafStatusText: string;
  readonly anafCheckedAt: string | null;
}

/** Formularul gol. Un partener persoană fizică se salvează exact așa, plus nume și CNP. */
export const EMPTY_PARTNER: PartnerInput = {
  name: '',
  taxCode: '',
  tradeRegisterNo: '',
  legalAddress: '',
  city: '',
  county: '',
  postalCode: '',
  phone: '',
  vatPayer: null,
  vatOnCollection: null,
  anafStatus: null,
  anafStatusText: '',
  anafCheckedAt: null,
};

/** Rândul, în forma în care îl editează formularul: `null` devine șir gol. */
export function toPartnerInput(partner: Partner): PartnerInput {
  return {
    name: partner.name,
    taxCode: partner.taxCode ?? '',
    tradeRegisterNo: partner.tradeRegisterNo ?? '',
    legalAddress: partner.legalAddress ?? '',
    city: partner.city ?? '',
    county: partner.county ?? '',
    postalCode: partner.postalCode ?? '',
    phone: partner.phone ?? '',
    vatPayer: partner.vatPayer,
    vatOnCollection: partner.vatOnCollection,
    anafStatus: partner.anafStatus,
    anafStatusText: partner.anafStatusText ?? '',
    anafCheckedAt: partner.anafCheckedAt,
  };
}

export interface PartnerFilters {
  readonly search?: string;
  readonly page: number;
}

export const PARTNERS_PAGE_SIZE = 25;

export const PARTNERS_QUERY_KEY = ['partners'] as const;

export const partnersQueryOptions = (filters: PartnerFilters) =>
  queryOptions({
    queryKey: [...PARTNERS_QUERY_KEY, filters] as const,
    queryFn: () => {
      const params = new URLSearchParams();
      if (filters.search) params.set('search', filters.search);
      params.set('page', String(filters.page));
      params.set('perPage', String(PARTNERS_PAGE_SIZE));

      return apiGet(
        `/partners?${params.toString()}`,
        z.object({
          items: z.array(partnerSchema),
          total: z.number(),
          page: z.number(),
          perPage: z.number(),
        }),
      );
    },
  });

export function createPartner(input: PartnerInput): Promise<Partner> {
  return apiPost('/partners', input, z.object({ partner: partnerSchema })).then(
    (body) => body.partner,
  );
}

export function updatePartner(id: string, input: PartnerInput): Promise<Partner> {
  return apiPatch(`/partners/${id}`, input, z.object({ partner: partnerSchema })).then(
    (body) => body.partner,
  );
}

/**
 * Partenerul cu codul ăsta — **găsit, sau creat pe loc**.
 *
 * Pentru încărcarea unui document pe un partener (#133): operatorul caută la ANAF, iar
 * partenerul se naște din răspuns fără să mai treacă prin Conta → Parteneri.
 *
 * ⚠️ **Nu suprascrie un partener existent** — vezi `EnsurePartnerUseCase` pe server. `created`
 * spune care dintre cele două s-a întâmplat, ca ecranul să poată zice „s-a adăugat" doar când
 * chiar s-a adăugat.
 */
export function ensurePartner(
  input: PartnerInput,
): Promise<{ partner: Partner; created: boolean }> {
  return apiPost(
    '/partners/ensure',
    input,
    z.object({ partner: partnerSchema, created: z.boolean() }),
  );
}
